import React, { useMemo, useState } from "react";
import { useShow, useCustomMutation, useCreate, useCustom, useNavigation } from "@refinedev/core";
import { Show } from "@refinedev/antd";
import {
  Card,
  Descriptions,
  Row,
  Col,
  Table,
  Tag,
  Space,
  Button,
  Modal,
  InputNumber,
  Input,
  message,
  DatePicker,
  Form,
  Divider,
  Alert,
} from "antd";
import dayjs from "dayjs";
import {
  PURCHASE_ORDER_BY_ID_QUERY,
  VALIDATE_PURCHASE_ORDER_BUDGET_MUTATION,
  APPROVE_PURCHASE_ORDER_STEP_MUTATION,
  REJECT_PURCHASE_ORDER_STEP_MUTATION,
  CREATE_INVOICE_FROM_PO_MUTATION,
  GET_INVOICES_BY_PO_NUMBER_QUERY,
} from "./queries";
import { SubmitButton } from "./components/SubmitButton";
import { ApprovalDetailsTable } from "../purchase-request/components/ApprovalDetailsTable";
import { FilePdfOutlined, FileImageOutlined, FileTextOutlined, FileOutlined } from "@ant-design/icons";
import { PurchaseOrderStatus, OrderItem, Attachment, ApprovalStepStatus } from "./interface";
import { CREATE_RECEIVING_MUTATION } from "../receiving/queries";
import { CreateReceivingInput, ReceivingItem } from "../receiving/interface";
import { UserInfo } from "../../components/UserInfo";

const StatusBadge: React.FC<{ status: PurchaseOrderStatus }> = ({ status }) => {
  const colorMap: Record<PurchaseOrderStatus, string> = {
    [PurchaseOrderStatus.DRAFT]: "default",
    [PurchaseOrderStatus.SUBMITTED]: "processing",
    [PurchaseOrderStatus.VALIDATED]: "purple",
    [PurchaseOrderStatus.PARTIALLY_APPROVED]: "warning",
    [PurchaseOrderStatus.APPROVED]: "success",
    [PurchaseOrderStatus.REJECTED]: "error",
    [PurchaseOrderStatus.SENT_TO_VENDOR]: "blue",
    [PurchaseOrderStatus.ACKNOWLEDGED]: "cyan",
    [PurchaseOrderStatus.IN_PROGRESS]: "gold",
    [PurchaseOrderStatus.PARTIALLY_DELIVERED]: "lime",
    [PurchaseOrderStatus.DELIVERED]: "green",
    [PurchaseOrderStatus.READY_FOR_INVOICING]: "orange",
    [PurchaseOrderStatus.COMPLETED]: "geekblue",
    [PurchaseOrderStatus.CANCELLED]: "red",
  };
  return <Tag color={colorMap[status] || "default"}>{status}</Tag>;
};

// Helper function to get appropriate icon based on mimeType
const getMimeTypeIcon = (mimeType: string) => {
  if (mimeType.includes("pdf")) {
    return <FilePdfOutlined />;
  } else if (mimeType.includes("image")) {
    return <FileImageOutlined />;
  } else if (mimeType.includes("text")) {
    return <FileTextOutlined />;
  }
  return <FileOutlined />;
};

// Helper function to format file size
const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

export const ShowPurchaseOrder: React.FC = () => {
  const [isValidateOpen, setIsValidateOpen] = useState(false);
  const [validateAmount, setValidateAmount] = useState<number | undefined>(undefined);
  const [validateRemarks, setValidateRemarks] = useState<string>("");

  const [isDecisionOpen, setIsDecisionOpen] = useState<"approve" | "reject" | null>(null);
  const [decisionRemarks, setDecisionRemarks] = useState<string>("");

  const [isCreateReceivingOpen, setIsCreateReceivingOpen] = useState(false);
  const [receivingForm] = Form.useForm();
  const [deliveryDate, setDeliveryDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState<dayjs.Dayjs | null>(null);

  const [supplierPortalInvoices, setSupplierPortalInvoices] = useState<any[]>([]);
  const [isInvoiceAlertVisible, setIsInvoiceAlertVisible] = useState(false);
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const { query } = useShow({
    resource: "purchaseOrders",
    meta: {
      gqlQuery: PURCHASE_ORDER_BY_ID_QUERY,
    },
  });

  console.log(query)

  const { data, isLoading } = query;
  const record = data?.data;

  const { mutate: validateMutate, isLoading: validating } = useCustomMutation();
  const { mutate: approveMutate, isLoading: approving } = useCustomMutation();
  const { mutate: rejectMutate, isLoading: rejecting } = useCustomMutation();
  const { mutateAsync: createInvoiceFromPO } = useCustomMutation();

  // Query for supplier portal invoices
  const { data: supplierPortalInvoicesData, isLoading: loadingInvoices } = useCustom({
    url: '',
    method: 'get',
    meta: {
      gqlQuery: GET_INVOICES_BY_PO_NUMBER_QUERY,
      gqlVariables: {
        poNumber: record?.orderNo || '',
      },
    },
    queryOptions: {
      enabled: !!record?.orderNo && record?.status === PurchaseOrderStatus.READY_FOR_INVOICING,
    },
  });

  const { mutateAsync: createReceiving, isLoading: creatingReceiving } = useCreate();
  const { show } = useNavigation();

  const handleSubmitSuccess = () => {
    // Refresh the data after successful submission
    query.refetch();
  };

  // Use the supplier portal invoices data from the query
  React.useEffect(() => {
    const invoices = supplierPortalInvoicesData?.data?.getInvoicesByPONumber || [];
    if (invoices.length > 0) {
      setSupplierPortalInvoices(invoices);
      // Auto-show alert when invoices are found and no invoice exists yet
      if (!record?.invoiceId) {
        setIsInvoiceAlertVisible(true);
      }
    }
  }, [supplierPortalInvoicesData, record?.invoiceId]);

  const handleCreateInvoice = async (supplierPortalInvoice: any) => {
    if (!record?.id) return;

    try {
      setIsCreatingInvoice(true);
      const result = await createInvoiceFromPO({
        url: "",
        method: "post",
        values: {
          poId: record.id,
          supplierPortalInvoice,
        },
        meta: { gqlMutation: CREATE_INVOICE_FROM_PO_MUTATION },
      });

      if (result.data) {
        message.success("Invoice created successfully");
        setIsInvoiceAlertVisible(false);
        // Redirect to the newly created invoice using Refine navigation
        show("invoices", result.data?.createInvoiceFromPO.id)
      }
    } catch (error) {
      console.error("Error creating invoice:", error);
      message.error("Failed to create invoice");
    } finally {
      setIsCreatingInvoice(false);
    }
  };

  const totalFromItems = useMemo(() => {
    if (!record?.items?.length) return 0;
    return record.items.reduce((s: number, it: OrderItem) => s + Number(it.totalPrice || 0), 0);
  }, [record?.items]);

  const currentPendingStep = useMemo(() => {
    const steps = (record?.approvals || []).slice().sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
    return steps.find((s: any) => s.stepStatus === ApprovalStepStatus.PENDING);
  }, [record?.approvals]);

  const handleValidateBudget = async () => {
    if (!record?.id) return;
    validateMutate(
      {
        url: "",
        method: "post",
        values: {
          input: {
            orderId: record.id,
            totalAmount: Number(validateAmount ?? totalFromItems),
            remarks: validateRemarks || undefined,
          },
        },
        meta: { gqlMutation: VALIDATE_PURCHASE_ORDER_BUDGET_MUTATION },
      },
      {
        onSuccess: () => {
          message.success("Budget validated");
          setIsValidateOpen(false);
          setValidateRemarks("");
          query.refetch();
        },
        onError: (e) => {
          console.error(e);
          message.error("Failed to validate budget");
        },
      },
    );
  };

  const handleApproveReject = async () => {
    if (!record?.id || !currentPendingStep?.roleSlug || !isDecisionOpen) return;
    const meta = isDecisionOpen === "approve" ? { gqlMutation: APPROVE_PURCHASE_ORDER_STEP_MUTATION } : { gqlMutation: REJECT_PURCHASE_ORDER_STEP_MUTATION };
    const mutate = isDecisionOpen === "approve" ? approveMutate : rejectMutate;
    mutate(
      {
        url: "",
        method: "post",
        values: {
          input: {
            orderId: record.id,
            roleSlug: currentPendingStep.roleSlug,
            remarks: decisionRemarks || undefined,
          },
        },
        meta,
      },
      {
        onSuccess: () => {
          message.success(isDecisionOpen === "approve" ? "Approved" : "Rejected");
          setIsDecisionOpen(null);
          setDecisionRemarks("");
          query.refetch();
        },
        onError: (e) => {
          console.error(e);
          message.error("Action failed");
        },
      },
    );
  };

  const handleCreateReceiving = async () => {
    if (!record?.id) return;

    try {
      const values = await receivingForm.validateFields();

      // Convert PO items to receiving items
      const receivingItems: ReceivingItem[] = (record.items || []).map((item: OrderItem) => ({
        id: "", // Will be generated by backend
        name: item.name,
        description: item.description,
        orderedQty: item.quantity,
        receivedQty: item.quantity, // Default to full quantity, can be adjusted later
        acceptedQty: item.quantity, // Default to full quantity, can be adjusted later
        rejectedQty: 0,
        unit: item.unit,
        unitPrice: item.unitPrice,
        sourceOrderItemId: item.id,
        tags: item.tags,
      }));

      const receivingData: CreateReceivingInput = {
        poId: record.id.toString(),
        poNo: record.orderNo,
        deliveryDate: values.deliveryDate.toDate(),
        expectedDeliveryDate: values.expectedDeliveryDate?.toDate(),
        vendorInfo: {
          vendorId: record.vendor.vendorId,
          companyName: record.vendor.companyName,
          contactPerson: record.vendor.contactPerson,
          email: record.vendor.email,
          phone: record.vendor.phone,
          address: record.vendor.address,
          tin: record.vendor.tin,
        },
        items: receivingItems,
        notes: values.notes,
      };

      const result = await createReceiving({
        resource: "receivings",
        values: receivingData,
        meta: { gqlMutation: CREATE_RECEIVING_MUTATION },
      });

      if (result.data) {
        message.success("Receiving entry created successfully");
        setIsCreateReceivingOpen(false);
        receivingForm.resetFields();
        // Redirect to the newly created receiving entry
        window.location.href = `/receivings/show/${result.data.id}`;
      }
    } catch (error) {
      console.error("Error creating receiving entry:", error);
      if (error instanceof Error) {
        message.error(`Failed to create receiving entry: ${error.message}`);
      } else {
        message.error("Failed to create receiving entry");
      }
    }
  };


  const itemColumns = [
    { title: "Name", dataIndex: "name" },
    { title: "Description", dataIndex: "description" },
    { title: "Qty", dataIndex: "quantity" },
    { title: "Unit", dataIndex: "unit" },
    {
      title: "Unit Price",
      dataIndex: "unitPrice",
      render: (v: number) => `${record?.currency || "ETB"} ${Number(v || 0).toLocaleString()}`,
    },
    {
      title: "Total",
      dataIndex: "totalPrice",
      render: (v: number) => `${record?.currency || "ETB"} ${Number(v || 0).toLocaleString()}`,
    },
    { title: "Expected Delivery", dataIndex: "expectedDeliveryDate", render: (v: string) => v ? dayjs(v).format("YYYY-MM-DD") : "-" },
  ];

  const canValidate = record?.status === PurchaseOrderStatus.SUBMITTED;
  const canDecide = record?.status === PurchaseOrderStatus.VALIDATED || record?.status === PurchaseOrderStatus.PARTIALLY_APPROVED;
  const canCreateReceiving = record?.status === PurchaseOrderStatus.APPROVED;
  const canCreateInvoice = record?.status === PurchaseOrderStatus.READY_FOR_INVOICING;

  // Show alert box when invoices are found and PO doesn't have an invoice yet
  const invoiceAlert = React.useMemo(() => {
    if (supplierPortalInvoices.length > 0 && canCreateInvoice && !record?.invoiceId) {
      return (
        <Alert
          message={`Supplier Portal Invoices Found (${supplierPortalInvoices.length})`}
          description={`Found ${supplierPortalInvoices.length} invoice(s) from supplier portal for this PO. Click "Get Invoice" to create local invoice(s).`}
          type="info"
          showIcon
          action={
            <Button
              type="primary"
              onClick={() => setIsInvoiceAlertVisible(true)}
            >
              Get Invoice ({supplierPortalInvoices.length} found)
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      );
    }
    return null;
  }, [supplierPortalInvoices, canCreateInvoice, record?.invoiceId]);

  // Show alert box when invoice is already created
  const invoiceCreatedAlert = React.useMemo(() => {
    if (record?.invoiceId) {
      return (
        <Alert
          message="Invoice Created"
          description="An invoice has been created for this purchase order."
          type="success"
          showIcon
          action={
            <Button
              type="primary"
              onClick={() => show("invoices", record.invoiceId)}
            >
              View Invoice
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      );
    }
    return null;
  }, [record?.invoiceId, show]);

  const getApiBaseUrl = () => {
    return import.meta.env.VITE_API_URL
  };

  const filePreviewUrl = (fileId: string) => {
    return `${getApiBaseUrl()}/file-storage/preview/${fileId}`;
  };

  return (
    <Show
      isLoading={isLoading}
      title={record ? `Purchase Order • ${record.orderNo}` : "Purchase Order"}
      headerButtons={
        record && (
          <Space>
            {record.status === PurchaseOrderStatus.DRAFT && (
              <SubmitButton
                orderId={record.id as string}
                order={record}
                fiscalYear={new Date().getFullYear()}
                buttonText="Submit"
                onSuccess={handleSubmitSuccess}
              />
            )}
            {canValidate && (
              <Button type="primary" onClick={() => { setValidateAmount(record?.totalAmount ?? totalFromItems); setIsValidateOpen(true); }}>
                Validate Budget
              </Button>
            )}
            {canDecide && (
              <>
                <Button type="primary" onClick={() => setIsDecisionOpen("approve")} loading={approving}>
                  Approve
                </Button>
                <Button danger onClick={() => setIsDecisionOpen("reject")} loading={rejecting}>
                  Reject
                </Button>
              </>
            )}
            {canCreateReceiving && (
              <Button type="primary" onClick={() => setIsCreateReceivingOpen(true)}>
                Create Receiving Entry
              </Button>
            )}
            {canCreateInvoice && supplierPortalInvoices.length > 0 && !record?.invoiceId && (
              <Button type="primary" onClick={() => setIsInvoiceAlertVisible(true)}>
                Get Invoice ({supplierPortalInvoices.length} found)
              </Button>
            )}
          </Space>
        )
      }
    >
      {/* Invoice Alert Box */}
      {invoiceAlert}
      {invoiceCreatedAlert}
      {record && (
        <>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card>
                <Descriptions column={2} layout="vertical" bordered>
                  <Descriptions.Item label="PO No">{record.orderNo}</Descriptions.Item>
                  <Descriptions.Item label="Status"><StatusBadge status={record.status} /></Descriptions.Item>
                  <Descriptions.Item label="Team">{record.teamId}</Descriptions.Item>
                   <Descriptions.Item label="Ordered By">
                    {record.orderedBy || record.createdBy ? (
                      <UserInfo userId={record.orderedBy || record.createdBy} />
                    ) : (
                      '-'
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Date Created">{record.dateCreated ? dayjs(record.dateCreated).format("YYYY-MM-DD") : ""}</Descriptions.Item>
                  <Descriptions.Item label="Order Deadline">{record.orderDeadline ? dayjs(record.orderDeadline).format("YYYY-MM-DD") : ""}</Descriptions.Item>
                  <Descriptions.Item label="Expected Delivery Date">{record.expectedDeliveryDate ? dayjs(record.expectedDeliveryDate).format("YYYY-MM-DD") : "-"}</Descriptions.Item>
                  <Descriptions.Item label="Vendor">{record.vendor?.companyName || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Payment Terms">{record.paymentTerms || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Shipping Method">{record.shippingInfo?.method || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Total Amount">{record.currency} {Number(record.totalAmount || 0).toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Tags">{(record.tags || []).join(", ") || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Notes" span={2}>{record.notes || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Terms and Conditions" span={2}>{record.termsAndConditions || "-"}</Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>

            <Col span={24}>
              <ApprovalDetailsTable approvals={record.approvals} />
            </Col>

            <Col span={24}>
              <Card title="Items">
                <Table dataSource={record.items} columns={itemColumns as any} rowKey={(r: any) => r.id} pagination={false}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={5}><strong>Total</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={1}><strong>{record.currency} {Number(totalFromItems || 0).toLocaleString()}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={2} colSpan={1}></Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
              </Card>
            </Col>

            <Col span={24}>
              <Card title="Attachments">
                <Space wrap>
                  {(record.attachments || []).map((attachment: Attachment) => {
                    return (
                      <div key={attachment.fileId} style={{ border: "1px solid #eee", padding: 8, borderRadius: 6 }}>
                        <Space>
                          {getMimeTypeIcon(attachment.mimeType)}
                          <a href={filePreviewUrl(attachment.fileId)} target="_blank" rel="noreferrer">
                            {attachment.originalName} {attachment.size > 0 && `(${formatBytes(attachment.size)})`}
                          </a>
                        </Space>
                      </div>
                    );
                  })}
                  {!record.attachments?.length && <div>No attachments</div>}
                  <div style={{ marginTop: 8, color: "#888" }}>
                    Upload/remove is available on Edit page.
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>

          <Modal
            title="Validate Budget"
            open={isValidateOpen}
            onOk={handleValidateBudget}
            onCancel={() => setIsValidateOpen(false)}
            confirmLoading={validating}
            okText="Validate"
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <div>Total Amount</div>
              <InputNumber
                value={validateAmount}
                onChange={(v) => setValidateAmount(Number(v || 0))}
                min={0}
                step={0.01}
                style={{ width: "100%" }}
              />
              <div>Remarks</div>
              <Input.TextArea rows={3} value={validateRemarks} onChange={(e) => setValidateRemarks(e.target.value)} />
            </Space>
          </Modal>

          <Modal
            title={isDecisionOpen === "approve" ? "Approve Order" : "Reject Order"}
            open={!!isDecisionOpen}
            onOk={handleApproveReject}
            onCancel={() => setIsDecisionOpen(null)}
            okText={isDecisionOpen === "approve" ? "Approve" : "Reject"}
            okButtonProps={{ danger: isDecisionOpen === "reject" }}
            confirmLoading={approving || rejecting}
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <div>Next Step Role</div>
              <Input value={currentPendingStep?.roleSlug || "-"} disabled />
              <div>Remarks</div>
              <Input.TextArea rows={3} value={decisionRemarks} onChange={(e) => setDecisionRemarks(e.target.value)} />
            </Space>
          </Modal>

          <Modal
            title="Create Receiving Entry"
            open={isCreateReceivingOpen}
            onOk={handleCreateReceiving}
            onCancel={() => setIsCreateReceivingOpen(false)}
            okText="Create Receiving Entry"
            confirmLoading={creatingReceiving}
            width={800}
          >
            <Form form={receivingForm} layout="vertical">
              <Card title="PO Summary" style={{ marginBottom: 16 }}>
                <Descriptions column={2}>
                  <Descriptions.Item label="PO Number">{record?.orderNo}</Descriptions.Item>
                  <Descriptions.Item label="Vendor">{record?.vendor?.companyName}</Descriptions.Item>
                  <Descriptions.Item label="Total Amount">{record?.currency} {Number(record?.totalAmount || 0).toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Status"><StatusBadge status={record?.status} /></Descriptions.Item>
                </Descriptions>
              </Card>

              <Card title="Items to be Received" style={{ marginBottom: 16 }}>
                <Table
                  dataSource={record?.items || []}
                  columns={[
                    { title: "Name", dataIndex: "name" },
                    { title: "Description", dataIndex: "description" },
                    { title: "Qty", dataIndex: "quantity" },
                    { title: "Unit", dataIndex: "unit" },
                    {
                      title: "Unit Price",
                      dataIndex: "unitPrice",
                      render: (v: number) => `${record?.currency || "ETB"} ${Number(v || 0).toLocaleString()}`,
                    },
                    {
                      title: "Total",
                      dataIndex: "totalPrice",
                      render: (v: number) => `${record?.currency || "ETB"} ${Number(v || 0).toLocaleString()}`,
                    },
                  ]}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={5}><strong>Total</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={1}><strong>{record?.currency} {Number(totalFromItems || 0).toLocaleString()}</strong></Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
              </Card>

              <Divider />

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Delivery Date"
                    name="deliveryDate"
                    rules={[{ required: true, message: "Delivery date is required" }]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      value={deliveryDate}
                      onChange={setDeliveryDate}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Expected Delivery Date"
                    name="expectedDeliveryDate"
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      value={expectedDeliveryDate}
                      onChange={setExpectedDeliveryDate}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Notes"
                name="notes"
              >
                <Input.TextArea rows={3} placeholder="Optional notes for the receiving entry" />
              </Form.Item>
            </Form>
          </Modal>

          {/* Invoice Alert Modal */}
          <Modal
            title="Supplier Portal Invoices Found"
            open={isInvoiceAlertVisible}
            onCancel={() => setIsInvoiceAlertVisible(false)}
            footer={null}
            width={800}
          >
            <div style={{ marginBottom: 16 }}>
              <p>Found {supplierPortalInvoices.length} invoice(s) from supplier portal for this PO:</p>
            </div>

            {supplierPortalInvoices.map((invoice: any, index: number) => (
              <Card key={index} style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <div><strong>Invoice Number:</strong> {invoice.invoiceNumber}</div>
                    <div><strong>Supplier:</strong> {invoice.supplier?.companyName}</div>
                    <div><strong>Total Amount:</strong> {invoice.total} {invoice.currency || 'ETB'}</div>
                    <div><strong>Status:</strong> {invoice.status}</div>
                  </Col>
                  <Col span={12}>
                    <div><strong>Invoice Date:</strong> {invoice.invoiceDate ? dayjs(invoice.invoiceDate).format('YYYY-MM-DD') : 'N/A'}</div>
                    <div><strong>GRN Number:</strong> {invoice.grnNumber || invoice.delivery?.grnNumber || 'N/A'}</div>
                    <div><strong>Items:</strong> {invoice.items?.length || 0}</div>
                  </Col>
                </Row>
                <div style={{ marginTop: 16 }}>
                  <Button
                    type="primary"
                    loading={isCreatingInvoice}
                    onClick={() => handleCreateInvoice(invoice)}
                  >
                    Create Invoice
                  </Button>
                </div>
              </Card>
            ))}
          </Modal>
        </>
      )}
    </Show>
  );
};